import { useEffect, useRef } from 'react';
import { useToast } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { ChargerError, ChargerStatus } from 'clipsal-cortex-types/src/api/api-evcharger';
import { useAppVisibility } from 'clipsal-cortex-utils/src/hooks/use-app-visibility';

import { useReduxDispatch } from '../../app/store';
import { streamEvents } from '../../common/api/api-helpers';
import { ENDPOINT, IS_NATIVE } from '../../common/constants';
import { trackDynatraceSseLifecycle, trackDynatraceStatusPollResult } from '../../utils/native/dynatrace-events';
import {
  commandConfirmationTimedOut,
  fetchEvChargerStatus,
  selectPendingCommandDeadline,
  selectPendingCommandFromStatus,
  syncLiveChargingReadings,
  syncSmartAmpState,
  updateControlModeFromSse,
  updateStatusFromSse,
} from '../ev-charger/dashboard/evChargerSlice';
import { selectEvChargers, selectSite } from '../site/siteSlice';
import { createChildAbortController, createStallWatchdog, wait } from './evse-stream-runtime';
import { selectSmartAmpData, setFirmwareUpdateStatus, setMeterValues } from './evseStreamSlice';
import { createLiveStatusTelemetry } from './live-status-telemetry';
import {
  deriveStateFromEvent,
  parseChargerStatusEvent,
  parseCloudStatusEvent,
  parseFirmwareUpdateStatusEvent,
  parseTransactionEvent,
  SmartAmpSseState,
} from './smart-amp-sse-helpers';

// Re-export pure helpers so existing call sites and tests keep their imports.
export {
  deriveStateFromEvent,
  parseCloudStatusEvent,
  parseChargerStatusEvent,
  parseInfo,
  parseFirmwareUpdateStatusEvent,
  parseTransactionEvent,
} from './smart-amp-sse-helpers';
export type {
  ChargerStatusUpdateEvent,
  CloudStatusUpdateEvent,
  ChargerTransactionEvent,
  FirmwareUpdateStatusEvent,
  SmartAmpSseState,
  SmartAmpStatus,
} from './smart-amp-sse-helpers';

/**
 * Reads the current SmartAmp SSE state from Redux.
 * Requires `useStartSmartAmpStream` to be active at a parent level (e.g. EVChargerHome)
 * for the state to be kept up to date.
 */
export function useSmartAmpSse(): SmartAmpSseState {
  return useSelector(selectSmartAmpData);
}

/**
 * Opens the SmartAmp SSE stream and dispatches state updates to Redux.
 * Should be called once at the EVChargerHome level so the stream remains
 * active across all child routes and the status is accessible app-wide.
 */

const STREAM_RETRY_DELAY_MS = 3_000;
// On native, Android WebView drops SSE chunks silently after the initial server ping.
// A shorter stall timeout makes the SSE loop reconnect aggressively while the polling
// fallback (below) bridges any gap. On web the stream is reliable; 60s avoids
// unnecessary reconnects.
const STREAM_STALL_TIMEOUT_MS = IS_NATIVE ? 20_000 : 60_000;
// On native, poll the REST status endpoint as a fallback for the unreliable SSE stream.
// Keeps charger status (online/offline, charging state, control_mode) fresh even when
// the WebView stalls. Note: SmartAmp derating state (loadActive/tempActive) is not
// available from the REST endpoint and continues to rely on SSE.
const NATIVE_POLL_INTERVAL_MS = 15_000;

/* istanbul ignore next -- @preserve */
export function useEVSEStream(): void {
  const dispatch = useReduxDispatch();
  const toast = useToast();
  const { t } = useTranslation();
  const evChargers = useSelector(selectEvChargers);
  const site = useSelector(selectSite);
  const siteDeviceId = evChargers[0]?.id?.toString() ?? null;
  const chargerId = evChargers[0]?.site_identifier ?? null;
  const isAppVisible = useAppVisibility();
  const controllerRef = useRef<AbortController | null>(null);
  const liveStatusTelemetryRef = useRef<ReturnType<typeof createLiveStatusTelemetry> | null>(null);
  if (!liveStatusTelemetryRef.current) liveStatusTelemetryRef.current = createLiveStatusTelemetry();
  const liveStatusTelemetry = liveStatusTelemetryRef.current;
  const connectedChargerIdRef = useRef<string | null>(null);
  const pendingCommandFromStatus = useSelector(selectPendingCommandFromStatus);
  const pendingCommandDeadline = useSelector(selectPendingCommandDeadline);

  // Fallback for a start/stop command whose resulting status never arrives over SSE.
  useEffect(() => {
    if (pendingCommandFromStatus === null || pendingCommandDeadline === null) return;
    const remainingMs = Math.max(0, pendingCommandDeadline - Date.now());
    const timeoutId = setTimeout(() => {
      dispatch(commandConfirmationTimedOut());
      toast({
        title: t('EVSE.command failed'),
        status: 'error',
        isClosable: true,
      });
    }, remainingMs);
    return () => clearTimeout(timeoutId);
  }, [pendingCommandFromStatus, pendingCommandDeadline, dispatch, toast, t]);

  // Native polling fallback — runs independently of the SSE stream.
  // Guarantees status refreshes within NATIVE_POLL_INTERVAL_MS when the Android
  // WebView drops SSE chunks after the initial server ping.
  useEffect(() => {
    // Keep commissioning UX intact: while site commissioning is PENDING,
    // DeviceStatus should remain in the pre-first-update flow (Almost there).
    if (!IS_NATIVE || !siteDeviceId || !isAppVisible || site.commissioning_status === 'PENDING') return;

    const pollStatus = async () => {
      try {
        const status = await dispatch(fetchEvChargerStatus(siteDeviceId)).unwrap();
        trackDynatraceStatusPollResult(
          'success',
          chargerId ?? siteDeviceId,
          liveStatusTelemetry.getStatusFreshnessMs(chargerId)
        );
        liveStatusTelemetry.recordStatusUpdate(
          chargerId,
          'native_poll',
          status.status,
          status.timestamp,
          {
            isScheduledChargeActive: status.is_scheduled_charge_active,
            scheduledChargeStartTime: status.scheduled_charge_start_time,
            scheduledChargeEndTime: status.scheduled_charge_end_time,
            controlMode: status.control_mode,
          },
          status.power
        );
        liveStatusTelemetry.recordSessionMetrics(chargerId, 'native_poll', {
          sessionStartedAt: status.latest_charge_session_start,
          sessionEndedAt: status.latest_charge_session_end,
          powerKw: status.power,
          energyKwh: status.energy_consumed,
        });
      } catch {
        trackDynatraceStatusPollResult(
          'failure',
          chargerId ?? siteDeviceId,
          liveStatusTelemetry.getStatusFreshnessMs(chargerId)
        );
      }
    };

    void pollStatus();
    const pollInterval = setInterval(() => {
      void pollStatus();
    }, NATIVE_POLL_INTERVAL_MS);

    return () => clearInterval(pollInterval);
  }, [siteDeviceId, chargerId, dispatch, isAppVisible, site.commissioning_status, liveStatusTelemetry]);

  useEffect(() => {
    if (!chargerId || !isAppVisible) return;

    // Abort any in-flight stream immediately so the visibility effect below
    // can start a fresh one as soon as the app returns to foreground.
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;

    (async () => {
      while (!controller.signal.aborted) {
        const { controller: attemptController, dispose } = createChildAbortController(controller.signal);
        const wasConnected = connectedChargerIdRef.current === chargerId;
        let attemptConnected = false;
        let disconnectReason: 'stream_ended' | 'network_error' | 'silence_timeout' = 'stream_ended';
        trackDynatraceSseLifecycle('sse_connection_started', chargerId);
        const stallWatchdog = createStallWatchdog({
          timeoutMs: STREAM_STALL_TIMEOUT_MS,
          platform: IS_NATIVE ? 'native' : 'web',
          isStopped: () => controller.signal.aborted,
          onStall: () => {
            disconnectReason = 'silence_timeout';
            trackDynatraceSseLifecycle('sse_silence_detected', chargerId, {
              reason: 'silence_timeout',
              silenceTimeoutMs: STREAM_STALL_TIMEOUT_MS,
              statusFreshnessMs: liveStatusTelemetry.getStatusFreshnessMs(chargerId),
            });
            attemptController.abort();
          },
        });

        stallWatchdog.reset();
        try {
          await streamEvents(
            `${ENDPOINT}/v1/evse/stream/${chargerId}`,
            (line) => {
              stallWatchdog.reset();
              // ChargerStatusUpdate — only event type with the `info` field
              // that carries SmartAmp derating flags.
              const statusEvent = parseChargerStatusEvent(line);
              if (statusEvent) {
                liveStatusTelemetry.recordStatusUpdate(
                  chargerId,
                  'sse',
                  statusEvent.status,
                  statusEvent.chargerSendingDate,
                  {
                    isScheduledChargeActive: statusEvent.is_scheduled_charge_active,
                    scheduledChargeStartTime: statusEvent.scheduled_charge_start_time,
                    scheduledChargeEndTime: statusEvent.scheduled_charge_end_time,
                    controlMode: statusEvent.control_mode,
                  }
                );
                dispatch(syncSmartAmpState(deriveStateFromEvent(statusEvent)));
                dispatch(
                  updateStatusFromSse({
                    status: statusEvent.status as ChargerStatus,
                    error: statusEvent.error as ChargerError,
                    vendorErrorCode: statusEvent.vendorErrorCode,
                    control_mode: statusEvent.control_mode,
                    is_scheduled_charge_active: statusEvent.is_scheduled_charge_active,
                    scheduled_charge_start_time: statusEvent.scheduled_charge_start_time,
                    scheduled_charge_end_time: statusEvent.scheduled_charge_end_time,
                  })
                );
                dispatch(syncLiveChargingReadings(statusEvent.status as ChargerStatus));
                return;
              }

              // StopTransaction + MeterValues — same `ChargerTransactionEventMessage`
              // schema: has status/error + transaction fields but NO `info`.
              const txEvent = parseTransactionEvent(line);
              if (txEvent) {
                // Intentionally skip status/error updates for MeterValues events.
                // MeterValues can stream frequently during an active session and
                // should only update usage metrics, not the dashboard status.
                // Status/error updates are not guaranteed on every event, so we
                // only trust StopTransaction for status/error in this branch.
                if (txEvent.event_type === 'StopTransaction') {
                  liveStatusTelemetry.recordStatusUpdate(chargerId, 'sse', txEvent.status, txEvent.chargerSendingDate);
                  dispatch(
                    updateStatusFromSse({
                      status: txEvent.status as ChargerStatus,
                      error: txEvent.error as ChargerError,
                      vendorErrorCode: txEvent.vendorErrorCode,
                    })
                  );
                  dispatch(syncLiveChargingReadings(txEvent.status as ChargerStatus));
                }

                liveStatusTelemetry.recordSessionMetrics(chargerId, 'sse', {
                  sessionStartedAt: txEvent.transactionStartDate,
                  sessionEndedAt: txEvent.transactionEndDate,
                  powerKw: txEvent.kwInstantaneousPower,
                  energyKwh: txEvent.kwhConsumed,
                  costAmount: txEvent.amountUsage,
                  currency: txEvent.currency,
                });

                // Only MeterValues should update the dashboard meter state.
                if (txEvent.event_type === 'MeterValues') {
                  dispatch(
                    setMeterValues({
                      amountUsage: txEvent.amountUsage,
                      currency: txEvent.currency,
                      kwhConsumed: txEvent.kwhConsumed,
                      kwInstantaneousPower: txEvent.kwInstantaneousPower,
                      maxAmps: txEvent.maxAmps,
                      chargingAmps: txEvent.chargingAmps,
                    })
                  );
                  // Restores simulated amps if a simulation is active; no-op otherwise.
                  dispatch(syncSmartAmpState());
                }
              }

              // CloudStatusUpdate — only event type with the `control_mode` field.
              const cloudStatusEvent = parseCloudStatusEvent(line);
              if (cloudStatusEvent?.control_mode !== undefined) {
                dispatch(
                  updateControlModeFromSse({
                    control_mode: cloudStatusEvent.control_mode,
                  })
                );
              }

              // FirmwareUpdateStatus — firmware update lifecycle events.
              const fwEvent = parseFirmwareUpdateStatusEvent(line);
              if (fwEvent) {
                dispatch(
                  setFirmwareUpdateStatus({
                    cloudStatus: fwEvent.cloudFirmwareStatus,
                    status: fwEvent.firmwareStatus ?? null,
                    firmwareVersion: fwEvent.firmwareVersion,
                  })
                );
              }
            },
            attemptController.signal,
            () => {
              attemptConnected = true;
              connectedChargerIdRef.current = chargerId;
              trackDynatraceSseLifecycle(wasConnected ? 'sse_reconnected' : 'sse_connected', chargerId);
            }
          );
        } catch (err) {
          if ((err as Error).name === 'AbortError') {
            // Stall-timer abort — fall through to reconnect delay below.
          } else {
            // TypeError: network error is expected when the server/proxy closes the
            // chunked connection (ERR_INCOMPLETE_CHUNKED_ENCODING). The while-loop
            // will reconnect after STREAM_RETRY_DELAY_MS — no action needed.
            const isExpectedNetworkDisconnect =
              err instanceof TypeError && (err as TypeError).message.toLowerCase().includes('network');
            if (isExpectedNetworkDisconnect) {
              disconnectReason = 'network_error';
              console.warn('[SmartAmpSse] stream disconnected, reconnecting…');
            } else {
              disconnectReason = 'network_error';
              console.error('[SmartAmpSse] stream error', err);
            }
          }
        }

        if (!controller.signal.aborted) {
          if (attemptConnected) {
            trackDynatraceSseLifecycle('sse_disconnected', chargerId, {
              reason: disconnectReason,
              statusFreshnessMs: liveStatusTelemetry.getStatusFreshnessMs(chargerId),
            });
          } else trackDynatraceSseLifecycle('sse_connection_failed', chargerId);
        }

        dispose();
        stallWatchdog.stop();

        // Stream ended (cleanly, via network error, or stall) — wait then reconnect
        // unless the component has been unmounted or the stream was aborted externally.
        if (!controller.signal.aborted) {
          await wait(STREAM_RETRY_DELAY_MS, controller.signal);
        }
      }
    })();

    return () => {
      controller.abort();
    };
    // isAppVisible intentionally included: aborts the stream when backgrounded and
    // opens a fresh one when the app returns to foreground.
  }, [chargerId, dispatch, isAppVisible, liveStatusTelemetry]);
}
